// featuredarticlesactions.tsx

import { createAsyncThunk } from '@reduxjs/toolkit';
import { sendGetRequest, sendPostRequest } from '../../../api/requestUtil';

// 1. HOMEPAGE FEED (no page param — returns shuffled/pinned feed)
export const getFeaturedArticlesList = createAsyncThunk(
  'get-featured-articles-list',
  async (): Promise<ServerPaginatorType> => {
    return sendGetRequest(`/api/articles/featured/`);
  }
);

// 2. PAGINATED FULL LIST (used by See All page — separate slice so it
//    never overwrites the homepage feed in the Redux store)
export const getFeaturedArticlesAll = createAsyncThunk(
  'get-featured-articles-all',
  async (page?: string | number): Promise<ServerPaginatorType> => {
    const p = page ?? 1;
    return sendGetRequest(`/api/articles/featured/?page=${p}`);
  }
);

// 2. DETAIL ACTION (Used on the Dedicated Article Page)
// Uses the single article endpoint: /api/articles/{id}/
export const getFeaturedArticleDetails = createAsyncThunk<
  BlogCard,
  string | number,
  { state: AppState; rejectValue: BlogPageDetailError }
>('get-featured-article-details', async (articleId, { getState, rejectWithValue }) => {
  try {
    // URL: /api/articles/{id}/ — send the token so staff can preview drafts
    const token = getState().user.authToken;
    return await sendGetRequest(`/api/articles/${articleId}/`, token);
  } catch (error: any) {
    return rejectWithValue({
      detail: error.response?.data?.detail || 'Failed to fetch article details',
    });
  }
});

// 3. ACTION FOR REVIEWS TAB
export const getFeaturedArticleReviews = createAsyncThunk<
  BlogReviewsListSuccessType,
  string | number
>('get-featured-article-reviews', async articleId => {
  // Explicitly building the assumed URL for the reviews endpoint
  const url = `/api/articles/${articleId}/reviews/`;
  return sendGetRequest(url);
});

// 4. ACTION FOR DISCUSSIONS TAB
export const getFeaturedArticleDiscussion = createAsyncThunk<
  BlogDiscussionsListSuccessType,
  string | number
>('get-featured-article-discussion', async articleId => {
  // Explicitly building the assumed URL for the discussions endpoint
  const url = `/api/articles/${articleId}/discussions/`;
  return sendGetRequest(url);
});

export const verifyArticleUrl = createAsyncThunk(
  'verify-article-url',
  async (url: string) => {
    return sendGetRequest(`/api/articles/url/?url=${url}`);
  }
);

export const postArticleSuggestion = createAsyncThunk<
  ArticleSuggestionType,
  ArticleSuggestionRequestType,
  { state: AppState; rejectValue: string }
>(
  'post-article-suggestion',
  async (body, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;
    try {
      return await sendPostRequest('/api/articles/suggestions/', body, token);
    } catch (error: any) {
      const msg =
        error?.detail ||
        error?.url?.[0] ||
        (Object.values(error ?? {}).flat() as string[])[0] ||
        'Failed to submit suggestion. Please try again.';
      return rejectWithValue(msg);
    }
  }
);
